<template>
  <a-form-item class="password-input">
    <label
      v-if="label"
      class="input-label password-input-label"
      :class="{required}"
      for="password"
    >{{label}}</label>
    <a-input-password
      name="password"
      v-decorator="[
        'password',
        {
          rules: [
            {
              required: this.required,
              message: $t('validation.required'),
            },
            {
              validator: validation && validatePassword,
            },
            {
              validator: validation && validateToNextPassword,
            },
          ],
        },
      ]"
      type="password"
      :placeholder="placeholder"
      @change="onChange"
    />
    <div v-if="securityBar" class="security-container">
      <div class="security-bar">
        <div
          v-for="item in securityMax"
          :key="item"
          class="security-item"
          :class="`security-lvl-${calculateSecurityLvlForItem(item)}`"
        ></div>
      </div>
      <span class="security-lvl-text" :class="`security-lvl-${security}-text`">
        {{
        securityText
        }}
      </span>
    </div>
  </a-form-item>
</template>

<script>
const SECURITY_MAX_VALUE = 3;

export default {
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    required: {
      type: Boolean,
      default: true,
    },
    securityBar: Boolean,
    validation: Boolean,
  },
  computed: {
    securityText() {
      switch (this.security) {
        case 1:
          return this.$t('low');
        case 2:
          return this.$t('medium');
        case 3:
          return this.$t('strong');
        default:
          return '';
      }
    },
    securityMax() {
      return SECURITY_MAX_VALUE;
    },
    label() {
      return this.theme.mode !== 'label' ? '' : this.$t('password');
    },
    placeholder() {
      return this.theme.mode === 'label' ? '' : this.$t('password');
    },
  },
  data() {
    return {
      security: 0,
    };
  },
  inject: {
    form: {
      type: Object,
    },
    theme: {
      type: Object,
      default: {},
    },
  },
  methods: {
    onChange({ target: { value } }) {
      this.updateSecurity(value);
      this.$emit('change', value);
    },
    checkAllRules(value) {
      const length = value.length > 7;
      const digits = /\d/.test(value);
      const cases =
        value !== value.toLowerCase() && value !== value.toUpperCase();

      return {
        length,
        digits,
        cases,
      };
    },
    getPasswordSecurityLevel(value) {
      const res = Object.values(this.checkAllRules(value));
      return res.filter(Boolean).length;
    },
    updateSecurity(value) {
      this.security = this.getPasswordSecurityLevel(value);
    },
    calculateSecurityLvlForItem(value) {
      return this.security < value ? 0 : this.security;
    },
    validatePassword(rule, value, callback) {
      const ruleValues = this.checkAllRules(value);
      const requiredRules = ['length', 'cases'];
      const allRequiredRulesValid = requiredRules.every(
        (ruleName) => ruleValues[ruleName],
      );
      if (value && !allRequiredRulesValid) {
        callback(this.$t('validation.password'));
      } else {
        callback();
      }
    },
    validateToNextPassword(rule, value, callback) {
      const { form } = this;
      const fieldsValue = form.getFieldsValue();
      const hasDirtyConfirmPasswordField =
        fieldsValue.confirmPassword !== undefined;

      if (value && hasDirtyConfirmPasswordField) {
        form.validateFields(['confirmPassword'], { force: true });
      }
      callback();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/colors.scss';

.password-input {
  flex: 1;
}

.input-label {
  text-align: left;
}

.security-container {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
}
.security-bar {
  display: flex;
  align-items: center;
}
.security-item {
  height: 4px;
  width: 32px;
  margin-right: 10px;
  border-radius: 2px;
}

.security-lvl-text {
  line-height: 4px;
}

.security-lvl-0-text {
  color: $grey;
}
.security-lvl-0 {
  background: $grey;
}
.security-lvl-1-text {
  color: $red;
}
.security-lvl-1 {
  background: $red;
}
.security-lvl-2-text {
  color: $orange;
}
.security-lvl-2 {
  background: $orange;
}
.security-lvl-3-text {
  color: green;
}
.security-lvl-3 {
  background: green;
}
</style>

<style lang="scss">
.password-input {
  .ant-form-item-children {
    display: flex;
    flex-direction: column;
  }
}
</style>